import { useState, useEffect } from "react";
import CallDialer from "./CallDialer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  sent: { label: "📤 Sent", color: "#9CA3AF" },
  delivered: { label: "📬 Delivered", color: "#5AC8D8" },
  opened: { label: "👁️ Opened", color: "#FFB347" },
  clicked: { label: "🖱️ Clicked", color: "#00DC78" },
  bounced: { label: "⚠️ Bounced", color: "#FCA5A5" },
  complained: { label: "🚫 Complaint", color: "#FCA5A5" },
};

const GrantsTab = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [toast, setToast] = useState(null);
  const [trainOpen, setTrainOpen] = useState(true);
  const [followUpReady, setFollowUpReady] = useState([]);
  const [resending, setResending] = useState(null);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialerCtx, setDialerCtx] = useState(null);

  const openGrantDialer = (g) => {
    setDialerCtx({
      title: `Call ${g.desk_name}`,
      subtitle: `${g.sponsor} · ${g.name}`,
      phone: g.desk_phone || "",
      email: g.desk_email || "",
      context: { type: "grant", record_id: g.grant_id, record_label: g.name },
      talking_points: g.talking_points || [],
      qa: [
        { q: "Tell me about Moovin Forward.", a: "DFW interior unit stabilization firm — Black-woman-owned, founded by Shannon Ware. We restore vacant residential units to inspection-ready in 48 hours to 21 days, with court-grade audit packets every job." },
        { q: "What stage is your business at?", a: "Pilot launches August 2026. Real DFW test property already in flight (5960 Siltstone Lane). Banker briefing, $1M/$2M insurance schedule (Thimble IBL-F3EV6263SC active), 22-clause MVSA template, OCC 2013-29 vendor profile — all built and audit-defensible." },
        { q: "How will the grant funds be used?", a: "Materials cushion that protects 1099 crew pay structure during institutional A/R lag. Crew gets paid weekly, on time, above market — even when the bank's net-45." },
        { q: "Why are you different from other make-ready firms?", a: "Two things: (1) Built like an institutional vendor from day one — receivers and bank examiners can audit us with zero open questions. (2) Texas TREC firewall — we sit outside brokerage/management/leasing, so banks can engage us with no conflict-of-interest exposure." },
        { q: "When do you need to hear back?", a: "Ahead of our August 2026 pilot launch. Any timeline you can share would help me sequence outreach to other grant programs." },
      ],
    });
    setDialerOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/grants`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/grants/follow-up-ready`, { credentials: "include" }),
      ]);
      if (r.ok) {
        const d = await r.json();
        setGrants(d.grants || []);
      }
      if (r2.ok) {
        const d2 = await r2.json();
        setFollowUpReady(d2.ready || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = (gid) => {
    window.open(`${BACKEND_URL}/api/admin/grants/${gid}/pdf`, "_blank");
  };

  const sendGrant = async (gid) => {
    if (!window.confirm("Send this grant application now?\n\nThe PDF will be attached automatically. You'll see delivery + open status appear over the next few hours/days.")) return;
    setSending(gid);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/grants/send`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grant_id: gid, recipient_email: overrideEmail.trim() || null, cover_note: coverNote.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setToast({ ok: true, msg: `Sent to ${d.actually_sent_to}. Tracking armed (Resend ID: ${d.resend_id.slice(0,12)}...).` });
        setOpenId(null);
        setOverrideEmail("");
        setCoverNote("");
        load();
      } else {
        setToast({ ok: false, msg: d.detail || "Send failed." });
      }
    } catch (e) {
      setToast({ ok: false, msg: "Network error." });
    }
    setSending(null);
    setTimeout(() => setToast(null), 7000);
  };

  const resendGrant = async (gid) => {
    const noteFromUser = window.prompt(
      "Optional follow-up note (will appear in the email body).\n\nLeave blank for a default 'Following up — wanted to make sure this hit the right desk' message.\n\nMax 2 resends per grant — pick up the phone after that.",
      ""
    );
    if (noteFromUser === null) return;  // user hit Cancel
    setResending(gid);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/grants/resend`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grant_id: gid, follow_up_note: noteFromUser.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setToast({ ok: true, msg: `Follow-up #${d.resend_count} sent to ${d.actually_sent_to}.` });
        load();
      } else {
        setToast({ ok: false, msg: d.detail || "Resend failed." });
      }
    } catch (e) {
      setToast({ ok: false, msg: "Network error." });
    }
    setResending(null);
    setTimeout(() => setToast(null), 7000);
  };

  return (
    <div data-testid="grants-tab" style={{ display: "grid", gap: "1.1rem" }}>
      <div>
        <h2 style={{ color: "#F3F4F6", margin: 0, fontSize: "1.5rem" }}>💰 Grants Pipeline</h2>
        <p style={{ color: "#9CA3AF", marginTop: 4, fontSize: "0.92rem" }}>
          6 seeded grants pre-targeted for Black-woman-owned DFW SMBs in your stabilization vertical. Each fires a PDF + emails the desk, with delivery + open + click tracking.
        </p>
      </div>

      {/* Training / Talking Points card */}
      <div data-testid="grants-train-card" style={{
        background: "linear-gradient(135deg, rgba(184,145,90,0.12) 0%, rgba(27,42,78,0.08) 100%)",
        border: "1px solid rgba(184,145,90,0.35)", borderLeft: "4px solid #B8915A",
        borderRadius: 8, padding: "1rem 1.2rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.6rem", cursor: "pointer" }}
             onClick={() => setTrainOpen(!trainOpen)}>
          <div>
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
              📖 Walk Me Through It
            </div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1rem", marginTop: 3 }}>
              How to use the Grants Pipeline (read once, then go)
            </div>
          </div>
          <button type="button" style={{ background: "transparent", border: "1px solid #B8915A", color: "#B8915A", padding: "0.35rem 0.75rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
            {trainOpen ? "Hide" : "Show"}
          </button>
        </div>
        {trainOpen && (
          <div style={{ marginTop: "0.8rem", color: "#D1D5DB", fontSize: "0.88rem", lineHeight: 1.55 }}>
            <p style={{ marginTop: 0 }}><strong style={{ color: "#86EFAC" }}>1. Read each card.</strong> The &ldquo;Why we fit&rdquo; + &ldquo;Talking points&rdquo; sections are pre-loaded with the exact language to use. Each grant gets a custom-tuned application &mdash; they&rsquo;re not the same letter copy-pasted.</p>
            <p><strong style={{ color: "#86EFAC" }}>2. Preview the PDF first.</strong> Hit <em>📝 Preview PDF</em> on any grant to read what&rsquo;s about to go out. If you want to add a personal note (e.g., &ldquo;I was referred by Hello Alice&rsquo;s spring cohort&rdquo;), use the cover note field before Send.</p>
            <p><strong style={{ color: "#86EFAC" }}>3. Hit Send.</strong> The PDF attaches automatically. You&rsquo;ll see <span style={{color:"#9CA3AF"}}>📤 Sent</span> → <span style={{color:"#5AC8D8"}}>📬 Delivered</span> → <span style={{color:"#FFB347"}}>👁️ Opened</span> → <span style={{color:"#00DC78"}}>🖱️ Clicked</span> as the grant desk moves through your email.</p>
            <p><strong style={{ color: "#86EFAC" }}>4. Follow-up rule.</strong> If <em>📬 Delivered</em> shows but <em>👁️ Opened</em> doesn&rsquo;t fire in 5 business days → resend with a short cover note (&ldquo;Following up &mdash; wanted to make sure this hit the right desk&rdquo;). If <em>👁️ Opened</em> fires but you don&rsquo;t hear back in 10 business days → that&rsquo;s your warm-follow-up window.</p>
            <p style={{ marginBottom: 0, padding: "8px 12px", background: "rgba(122,143,126,0.15)", border: "1px solid rgba(122,143,126,0.4)", borderRadius: 6 }}>
              <strong style={{ color: "#86EFAC" }}>⚠️ ONE-TIME SETUP (Shannon does this in Resend):</strong> Resend dashboard → Webhooks → Add endpoint <code style={{ background: "rgba(0,0,0,0.3)", padding: "1px 5px", borderRadius: 3 }}>https://moovinforward.org/api/webhooks/resend</code> → Subscribe to: email.delivered, email.opened, email.clicked, email.bounced, email.complained. Without this, sends still work but you won&rsquo;t see live tracking badges update.
            </p>
          </div>
        )}
      </div>

      {/* Follow-up ready banner */}
      {followUpReady.length > 0 && (
        <div data-testid="grants-followup-banner" style={{
          background: "linear-gradient(135deg, rgba(255,179,71,0.15) 0%, rgba(27,42,78,0.1) 100%)",
          border: "1px solid rgba(255,179,71,0.5)", borderLeft: "4px solid #FFB347",
          borderRadius: 8, padding: "0.9rem 1.2rem"
        }}>
          <div style={{ fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#FFB347", fontWeight: 700 }}>
            🔔 Follow-Up Window Open
          </div>
          <div style={{ color: "#F3F4F6", fontWeight: 600, fontSize: "0.95rem", marginTop: 4 }}>
            {followUpReady.length === 1
              ? "1 grant has gone 7+ days without an open. Time for a warm follow-up."
              : `${followUpReady.length} grants have gone 7+ days without an open. Time for warm follow-ups.`}
          </div>
          <div style={{ color: "#D1D5DB", fontSize: "0.82rem", marginTop: 6, lineHeight: 1.5 }}>
            {followUpReady.map(f => (
              <div key={f.grant_id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                <span><strong>{f.grant_name}</strong> · delivered {f.days_since_delivered}d ago{f.resend_count > 0 ? ` · ${f.resend_count} resend${f.resend_count > 1 ? 's' : ''} so far` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="grants-toast" style={{
          background: toast.ok ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)",
          border: `1px solid ${toast.ok ? "rgba(34,197,94,0.4)" : "rgba(220,38,38,0.4)"}`,
          color: toast.ok ? "#86EFAC" : "#FCA5A5",
          padding: "0.7rem 1rem", borderRadius: 6, fontSize: "0.9rem"
        }}>
          {toast.ok ? "✓ " : "⚠ "}{toast.msg}
        </div>
      )}

      {loading && <div style={{ color: "#9CA3AF" }}>Loading grants…</div>}

      <div style={{ display: "grid", gap: "1rem" }}>
        {grants.map((g) => {
          const t = g.tracking || {};
          const statusKey = t.status || "";
          const status = STATUS_LABEL[statusKey];
          const isOpen = openId === g.grant_id;
          return (
            <div key={g.grant_id} data-testid={`grant-card-${g.grant_id}`} style={{
              background: "rgba(15,23,42,0.6)", border: "1px solid rgba(184,145,90,0.25)",
              borderRadius: 10, padding: "1.1rem 1.3rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.6rem", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 320px", minWidth: 240 }}>
                  <div style={{ fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
                    {g.sponsor} · Fit {g.fit_score}
                  </div>
                  <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1.05rem", marginTop: 3 }}>{g.name}</div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.84rem", marginTop: 4 }}>
                    {g.amount_band} · {g.deadline_cycle}
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.78rem", marginTop: 4 }}>
                    Desk: <span style={{ color: "#D1D5DB" }}>{g.desk_name}</span> · {g.desk_email}
                  </div>
                </div>
                {status && (
                  <div data-testid={`grant-status-${g.grant_id}`} style={{
                    padding: "0.3rem 0.7rem", borderRadius: 999, border: `1px solid ${status.color}`,
                    color: status.color, fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap"
                  }}>
                    {status.label}{t.open_count > 1 ? ` (${t.open_count}x)` : ""}
                  </div>
                )}
              </div>

              {/* Why we fit + Talking points */}
              <div style={{ marginTop: "0.8rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
                <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 6, padding: "0.65rem 0.85rem" }}>
                  <div style={{ color: "#86EFAC", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>✅ Why We Fit</div>
                  <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#D1D5DB", fontSize: "0.82rem", lineHeight: 1.5 }}>
                    {(g.why_we_fit || []).map((b, i) => <li key={i} style={{ marginBottom: 3 }}>{b}</li>)}
                  </ul>
                </div>
                <div style={{ background: "rgba(184,145,90,0.06)", border: "1px solid rgba(184,145,90,0.25)", borderRadius: 6, padding: "0.65rem 0.85rem" }}>
                  <div style={{ color: "#B8915A", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>🎤 Talking Points</div>
                  <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#D1D5DB", fontSize: "0.82rem", lineHeight: 1.5 }}>
                    {(g.talking_points || []).map((b, i) => <li key={i} style={{ marginBottom: 3 }}>{b}</li>)}
                  </ul>
                </div>
              </div>

              <div style={{ marginTop: "0.9rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button type="button" onClick={() => downloadPdf(g.grant_id)}
                        data-testid={`grant-preview-${g.grant_id}`}
                        style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A", padding: "0.55rem 1rem", borderRadius: 5, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                  📝 Preview PDF
                </button>
                <button type="button" onClick={() => setOpenId(isOpen ? null : g.grant_id)}
                        data-testid={`grant-prepare-${g.grant_id}`}
                        style={{ background: "#B8915A", color: "#1b2a4e", border: "1px solid #8a6c3e", padding: "0.55rem 1.2rem", borderRadius: 5, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                  {isOpen ? "Cancel" : (t.sent_at ? "📧 Send Again →" : "📧 Send →")}
                </button>
                {t.sent_at && (
                  <button type="button" onClick={() => resendGrant(g.grant_id)}
                          disabled={resending === g.grant_id}
                          data-testid={`grant-resend-${g.grant_id}`}
                          style={{ background: resending === g.grant_id ? "#4B5563" : "#FFB347", color: "#1b2a4e", border: "1px solid #c98c2a", padding: "0.55rem 1.1rem", borderRadius: 5, fontWeight: 700, fontSize: "0.85rem", cursor: resending === g.grant_id ? "not-allowed" : "pointer" }}>
                    {resending === g.grant_id ? "Sending…" : "🔁 Follow-Up"}
                  </button>
                )}
                <button type="button" onClick={() => openGrantDialer(g)}
                        data-testid={`grant-call-${g.grant_id}`}
                        style={{ background: "transparent", color: "#86EFAC", border: "1px solid #86EFAC", padding: "0.55rem 1rem", borderRadius: 5, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                  📞 Call
                </button>
                {t.sent_at && (
                  <span style={{ color: "#6B7280", fontSize: "0.74rem", alignSelf: "center" }}>
                    Last sent {t.sent_at.slice(0,16).replace("T"," ")} UTC
                  </span>
                )}
              </div>

              {isOpen && (
                <div data-testid={`grant-send-panel-${g.grant_id}`} style={{
                  marginTop: "0.85rem", padding: "0.85rem 1rem",
                  background: "rgba(27,42,78,0.4)", border: "1px solid rgba(184,145,90,0.4)", borderRadius: 6
                }}>
                  <div style={{ color: "#D1D5DB", fontSize: "0.78rem", marginBottom: 6 }}>
                    Sending to <strong>{g.desk_email}</strong> by default. Override below if your contact email differs.
                  </div>
                  <input type="email" placeholder={`Override email (optional — default: ${g.desk_email})`}
                         value={overrideEmail} onChange={(e) => setOverrideEmail(e.target.value)}
                         data-testid={`grant-override-${g.grant_id}`}
                         style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.5rem 0.7rem", borderRadius: 5, fontSize: "0.86rem", marginBottom: 6 }} />
                  <textarea placeholder="Optional personal cover note (e.g., 'I'm a Hello Alice cohort member following up on the spring announcement')"
                            value={coverNote} onChange={(e) => setCoverNote(e.target.value)}
                            rows={2}
                            data-testid={`grant-note-${g.grant_id}`}
                            style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.5rem 0.7rem", borderRadius: 5, fontSize: "0.84rem", resize: "vertical", fontFamily: "inherit" }} />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <button type="button" onClick={() => sendGrant(g.grant_id)}
                            disabled={sending === g.grant_id}
                            data-testid={`grant-send-confirm-${g.grant_id}`}
                            style={{ background: sending === g.grant_id ? "#4B5563" : "#00DC78", color: "#0F172A", border: "none", padding: "0.55rem 1.4rem", borderRadius: 5, fontWeight: 700, fontSize: "0.88rem", cursor: sending === g.grant_id ? "not-allowed" : "pointer" }}>
                      {sending === g.grant_id ? "Sending…" : "✓ Confirm & Send Now"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {dialerCtx && (
        <CallDialer open={dialerOpen} onClose={() => setDialerOpen(false)} {...dialerCtx} />
      )}
    </div>
  );
};

export default GrantsTab;
